import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Settings,
  RefreshCw,
  Search,
  X,
  Inbox,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  CalendarCheck,
  CalendarClock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import BlueHeader from '../components/BlueHeader';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const inputClass =
  'w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700';

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white text-gray-700 ' +
  'border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes tc-modal-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes tc-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes tc-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
.tc-modal-in { animation: tc-modal-in .18s ease-out; }
.tc-fade-in { animation: tc-fade-in .15s ease-out; }
.tc-toast-in { animation: tc-toast-in .2s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .tc-modal-in, .tc-fade-in, .tc-toast-in { animation: none; }
}
`;

const EMPTY_QUOTA = { quota_mensuel: '', quota_annuel: '', actif: true, description: '' };

/* ------------------------------------------------------------------ */
/*  Petits composants                                                  */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value, tone = 'bg-blue-50 text-blue-600' }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-semibold leading-tight text-gray-900">{value}</div>
      <div className="truncate text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const getPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

const Pagination = ({ current, total, from, to, count, pageSize, onPageSize, onChange }) => (
  <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
      <span>
        Affichage de <span className="font-medium text-gray-700">{from}</span> à{' '}
        <span className="font-medium text-gray-700">{to}</span> sur{' '}
        <span className="font-medium text-gray-700">{count}</span>
      </span>
      <label className="flex items-center gap-2">
        Par page
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          {[10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </div>
    {total > 1 && (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Page précédente"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPages(current, total).map((page, i) =>
          page === '…' ? (
            <span key={`dots-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onChange(page)}
              aria-current={current === page ? 'page' : undefined}
              className={`h-9 min-w-[2.25rem] rounded-lg border px-3 text-sm font-medium transition-colors ${
                current === page
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Page suivante"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    )}
  </div>
);

const Modal = ({ title, subtitle, onClose, maxWidth = 'max-w-md', children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="tc-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`tc-modal-in flex max-h-[92vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl`}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-blue-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Toggle = ({ checked, onChange, label, description }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex w-full items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50"
  >
    <span>
      <span className="block text-sm font-medium text-gray-700">{label}</span>
      {description && <span className="block text-xs text-gray-500">{description}</span>}
    </span>
    <span
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </span>
  </button>
);

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const TypesConge = () => {
  const { user } = useAuth();
  const [typesConge, setTypesConge] = useState([]);
  const [quotas, setQuotas] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingQuotaId, setOpeningQuotaId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [selectedTypeForQuota, setSelectedTypeForQuota] = useState(null);
  const [formData, setFormData] = useState({
    Libelle_conge: ''
  });
  const [quotaData, setQuotaData] = useState(EMPTY_QUOTA);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  /* ---------- Chargement ---------- */
  const loadQuotas = async () => {
    const quotasResponse = await http.get('/api/type-conge-quota');
    const quotasMap = {};
    quotasResponse.data.forEach((q) => {
      quotasMap[q.idtype_conge] = q;
    });
    setQuotas(quotasMap);
  };

  const fetchTypesConge = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/conges/types');
      setTypesConge(response.data || []);

      // Récupérer les quotas
      try {
        await loadQuotas();
      } catch (error) {
        console.error('Erreur récupération quotas:', error);
        setQuotas({});
      }
    } catch (error) {
      console.error('Erreur récupération types congé:', error);
      setTypesConge([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypesConge();
  }, []);

  /* ---------- Types de congé ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      if (editingType) {
        await http.put(`/api/conges/types/${editingType.idtype_conge}`, formData);
        notify('success', 'Type de congé mis à jour avec succès');
      } else {
        await http.post('/api/conges/types', formData);
        notify('success', 'Type de congé créé avec succès');
      }
      setShowModal(false);
      setEditingType(null);
      setFormData({ Libelle_conge: '' });
      fetchTypesConge();
    } catch (error) {
      console.error('Erreur soumission:', error);
      notify('error', 'Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      Libelle_conge: type.Libelle_conge
    });
    setShowModal(true);
  };

  const handleDelete = (type) => {
    setConfirmState({
      title: 'Supprimer ce type de congé ?',
      message: `« ${type.Libelle_conge} » sera supprimé définitivement. Cette action est irréversible.`,
      onConfirm: async () => {
        try {
          await http.delete(`/api/conges/types/${type.idtype_conge}`);
          notify('success', 'Type de congé supprimé avec succès');
          fetchTypesConge();
        } catch (error) {
          console.error('Erreur suppression:', error);
          notify('error', 'Erreur lors de la suppression');
        }
      }
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({ Libelle_conge: '' });
  };

  /* ---------- Quotas ---------- */
  const handleOpenQuotaModal = async (type) => {
    setSelectedTypeForQuota(type);
    setOpeningQuotaId(type.idtype_conge);
    try {
      const response = await http.get(`/api/type-conge-quota/${type.idtype_conge}`);
      setQuotaData({
        quota_mensuel: response.data.quota_mensuel || '',
        quota_annuel: response.data.quota_annuel || '',
        actif: response.data.actif !== undefined ? response.data.actif : true,
        description: response.data.description || ''
      });
    } catch (error) {
      // Si pas de quota configuré, utiliser des valeurs par défaut
      setQuotaData({
        quota_mensuel: '2.5',
        quota_annuel: '30',
        actif: true,
        description: ''
      });
    } finally {
      setOpeningQuotaId(null);
    }
    setShowQuotaModal(true);
  };

  const handleCloseQuotaModal = () => {
    setShowQuotaModal(false);
    setSelectedTypeForQuota(null);
    setQuotaData(EMPTY_QUOTA);
  };

  const handleSaveQuota = async (e) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      await http.put(`/api/type-conge-quota/${selectedTypeForQuota.idtype_conge}`, quotaData);
      notify('success', 'Quota configuré avec succès');
      // Rafraîchir les quotas
      await loadQuotas();
      setShowQuotaModal(false);
      setSelectedTypeForQuota(null);
      setQuotaData(EMPTY_QUOTA);
    } catch (error) {
      console.error('Erreur sauvegarde quota:', error);
      notify('error', 'Erreur lors de la sauvegarde du quota');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Statut d'un type ---------- */
  const getStatus = (type) => {
    const quota = quotas[type.idtype_conge];
    if (quota && quota.actif) return 'active';
    if (quota && !quota.actif) return 'inactive';
    return 'none';
  };

  /* ---------- Filtrage + pagination ---------- */
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return typesConge.filter((type) => {
      const matchSearch =
        !term ||
        (type.Libelle_conge || '').toLowerCase().includes(term) ||
        String(type.idtype_conge).includes(term);
      if (!matchSearch) return false;
      return statusFilter === 'all' || getStatus(type) === statusFilter;
    });
  }, [typesConge, quotas, search, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfLast = safePage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const nbActifs = typesConge.filter((t) => getStatus(t) === 'active').length;
  const nbInactifs = typesConge.filter((t) => getStatus(t) === 'inactive').length;
  const nbNonConfigures = typesConge.filter((t) => getStatus(t) === 'none').length;
  const hasFilters = search || statusFilter !== 'all';

  const tabs = [
    { id: 'all', label: 'Tous', count: typesConge.length },
    { id: 'active', label: 'Actifs', count: nbActifs },
    { id: 'inactive', label: 'Inactifs', count: nbInactifs },
    { id: 'none', label: 'Non configurés', count: nbNonConfigures }
  ];

  // Aide : équivalence mensuel → annuel
  const mensuel = parseFloat(quotaData.quota_mensuel);
  const annuelEquivalent = !Number.isNaN(mensuel) && mensuel > 0 ? Math.round(mensuel * 12 * 10) / 10 : null;

  /* ---------- Rendu ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{keyframes}</style>

      <BlueHeader
        title="Types de congé"
        subtitle="Gérer les types de congés disponibles"
        actions={[
          {
            label: 'Nouveau type',
            icon: Plus,
            onClick: () => setShowModal(true),
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchTypesConge,
            primary: false
          }
        ]}
      />

      <div className="space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {/* Statistiques */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Calendar} label="Types de congé" value={loading ? '–' : typesConge.length} />
          <StatCard
            icon={CalendarCheck}
            label="Quotas actifs"
            value={loading ? '–' : nbActifs}
            tone="bg-green-50 text-green-600"
          />
          <StatCard
            icon={CalendarClock}
            label="À configurer"
            value={loading ? '–' : nbNonConfigures}
            tone="bg-amber-50 text-amber-600"
          />
        </div>

        {/* Tableau */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Liste des types de congé</h3>
              <p className="mt-0.5 text-sm text-gray-500">
                Créez les types, puis configurez leur quota d'attribution automatique.
              </p>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un type de congé…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-9 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 lg:w-72"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filtres d'état */}
          <div className="overflow-x-auto border-b border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-6">
            <div role="tablist" className="inline-flex rounded-xl bg-gray-100 p-1">
              {tabs.map((tab) => {
                const active = statusFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setStatusFilter(tab.id)}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all ${
                      active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        active ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-6 px-6 py-4">
                  <div className="h-4 w-8 animate-pulse rounded bg-gray-100" />
                  <div className="h-4 flex-1 animate-pulse rounded bg-gray-100" />
                  <div className="h-5 w-24 animate-pulse rounded-full bg-gray-100" />
                  <div className="h-8 w-24 animate-pulse rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                {hasFilters ? <Search className="h-7 w-7" /> : <Inbox className="h-7 w-7" />}
              </div>
              <h4 className="text-base font-semibold text-gray-800">
                {hasFilters ? 'Aucun résultat' : 'Aucun type de congé trouvé'}
              </h4>
              <p className="mt-1 max-w-sm text-sm text-gray-500">
                {hasFilters
                  ? 'Aucun type ne correspond à votre recherche ou à ce filtre.'
                  : 'Créez votre premier type de congé (congé payé, RTT…) pour commencer.'}
              </p>
              <div className="mt-5">
                {hasFilters ? (
                  <button
                    onClick={() => {
                      setSearch('');
                      setStatusFilter('all');
                    }}
                    className={secondaryBtn}
                  >
                    Réinitialiser les filtres
                  </button>
                ) : (
                  <button onClick={() => setShowModal(true)} className={primaryBtn}>
                    <Plus className="h-4 w-4" />
                    Nouveau type
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">Libellé</th>
                    <th className="px-6 py-3">Quota</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentItems.map((type) => {
                    const quota = quotas[type.idtype_conge];
                    const status = getStatus(type);
                    return (
                      <tr key={type.idtype_conge} className="transition-colors hover:bg-blue-50/40">
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">#{type.idtype_conge}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                              <Calendar className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-medium text-gray-900">{type.Libelle_conge}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {status === 'active' ? (
                            <div className="flex flex-wrap gap-1.5">
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
                                {quota.quota_mensuel}j/mois
                              </span>
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                                {quota.quota_annuel}j/an
                              </span>
                            </div>
                          ) : status === 'inactive' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                              <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                              Inactif
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-800">
                              <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                              Non configuré
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => handleOpenQuotaModal(type)}
                              disabled={openingQuotaId === type.idtype_conge}
                              className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100 disabled:opacity-60"
                              title="Configurer le quota"
                              aria-label="Configurer le quota"
                            >
                              {openingQuotaId === type.idtype_conge ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Settings className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(type)}
                              className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100"
                              title="Modifier"
                              aria-label="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(type)}
                              className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                              title="Supprimer"
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <Pagination
              current={safePage}
              total={totalPages}
              from={indexOfFirst + 1}
              to={Math.min(indexOfLast, filtered.length)}
              count={filtered.length}
              pageSize={pageSize}
              onPageSize={setPageSize}
              onChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* Modal type de congé */}
      {showModal && (
        <Modal
          title={`${editingType ? 'Modifier' : 'Créer'} Type de congé`}
          subtitle="Ce libellé sera proposé lors des demandes de congé."
          onClose={handleCloseModal}
        >
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5">
              <label className={labelClass}>Libellé du congé</label>
              <input
                type="text"
                autoFocus
                value={formData.Libelle_conge}
                onChange={(e) => setFormData({ ...formData, Libelle_conge: e.target.value })}
                className={inputClass}
                placeholder="Ex: Congé payé, RTT, etc."
                required
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
              <button type="button" onClick={handleCloseModal} className={secondaryBtn}>
                Annuler
              </button>
              <button type="submit" disabled={saving} className={primaryBtn}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingType ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de configuration du quota */}
      {showQuotaModal && (
        <Modal
          title={`Configurer le quota - ${selectedTypeForQuota?.Libelle_conge || ''}`}
          subtitle="Définissez les jours attribués pour ce type de congé."
          onClose={handleCloseQuotaModal}
        >
          <form onSubmit={handleSaveQuota} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Quota mensuel (jours)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="31"
                    autoFocus
                    value={quotaData.quota_mensuel}
                    onChange={(e) => setQuotaData({ ...quotaData, quota_mensuel: e.target.value })}
                    className={inputClass}
                    placeholder="Ex: 2.5"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Quota annuel (jours)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="365"
                    value={quotaData.quota_annuel}
                    onChange={(e) => setQuotaData({ ...quotaData, quota_annuel: e.target.value })}
                    className={inputClass}
                    placeholder="Ex: 30"
                    required
                  />
                </div>
              </div>
              <p className="-mt-1 text-xs text-gray-500">
                Le quota mensuel est attribué automatiquement chaque mois ; le quota annuel est l'estimation pour l'année.
                {annuelEquivalent !== null && (
                  <span className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700">
                      {quotaData.quota_mensuel} j/mois ≈ {annuelEquivalent} j/an
                    </span>
                    {String(annuelEquivalent) !== String(quotaData.quota_annuel) && (
                      <button
                        type="button"
                        onClick={() => setQuotaData({ ...quotaData, quota_annuel: String(annuelEquivalent) })}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Utiliser {annuelEquivalent} comme quota annuel
                      </button>
                    )}
                  </span>
                )}
              </p>

              <Toggle
                checked={!!quotaData.actif}
                onChange={(v) => setQuotaData({ ...quotaData, actif: v })}
                label="Activer l'attribution automatique"
                description="Les jours sont ajoutés chaque mois aux soldes des employés"
              />

              <div>
                <label className={labelClass}>
                  Description <span className="font-normal text-gray-400">(optionnelle)</span>
                </label>
                <textarea
                  value={quotaData.description}
                  onChange={(e) => setQuotaData({ ...quotaData, description: e.target.value })}
                  rows="2"
                  className={inputClass}
                  placeholder="Description optionnelle"
                />
              </div>
            </div>
            <div className="flex shrink-0 justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
              <button type="button" onClick={handleCloseQuotaModal} className={secondaryBtn}>
                Annuler
              </button>
              <button type="submit" disabled={saving} className={primaryBtn}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Enregistrer
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirmation de suppression (remplace confirm) */}
      {confirmState && (
        <Modal title={confirmState.title} onClose={() => setConfirmState(null)}>
          <div className="flex items-start gap-3 px-6 py-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <p className="pt-1 text-sm text-gray-600">{confirmState.message}</p>
          </div>
          <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
            <button type="button" onClick={() => setConfirmState(null)} className={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                const { onConfirm } = confirmState;
                setConfirmState(null);
                onConfirm();
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
        </Modal>
      )}

      {/* Notifications */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const styles = {
            success: { box: 'border-green-200', icon: CheckCircle2, color: 'text-green-600' },
            error: { box: 'border-red-200', icon: AlertCircle, color: 'text-red-600' },
            warning: { box: 'border-amber-200', icon: AlertTriangle, color: 'text-amber-600' }
          }[t.type];
          const Icon = styles.icon;
          return (
            <div
              key={t.id}
              className={`tc-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg ${styles.box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles.color}`} />
              <p className="flex-1 text-sm text-gray-800">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TypesConge;